import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, FieldArray, FormikHelpers } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { ContentChannelOption } from "../../components/Orders/ChannelMultiSelect";
import {
    OrderProjectValues,
    OrderProProcess,
    toStringValue,
    createOrderWithProjects,
    updateOrder,
    updateExistingProject,
    createNewProject,
    unlinkDeletedProjects,
    syncOrderPros,
    buildOrderWithProjectsPayload,
    orderProjectProductSet,
} from "./helper";
import { getBrandOrder, parseIntakeDocument, createIntakeOrder } from "../../api/endpoints";
import {
    useContentTypes,
    useChannels,
    useRmnBrands,
    useEnrichedProjects,
    useSOWsByBrand,
    usePricingPreview,
    PricingPreviewState,
} from "../../hooks/useOrderFormData";
import { useUser } from "../../hooks/useUser";
import { isAM, isAdmin, isBrand, isInternal, isRMN } from "../../utils";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";

// Casting lifecycle order (mirrors the backend PROCESS_STATUS_RANK). Used to
// preview which projects will be bumped FORWARD when merged onto one selection
// (the backend converges a shared selection's projects to their most-advanced
// stage). Kept in sync with brandProjects.js BRAND_PROJECT_STATUSES.
const PROJECT_STATUS_RANK: Record<string, number> = {
    "Pending Approval": 0,
    "Waiting For Brief": 1,
    "Brief Review": 2,
    "Brief Denied": 2,
    "Expert Proposals": 3,
    "Experts Proposal Review": 4,
    "Experts Proposal Review Denied": 4,
    "Experts Selection": 5,
    Scheduling: 6,
    "Pre-Production": 7,
    Filming: 8,
    Editing: 9,
    "Video Review": 10,
    "Final Delivered": 11,
};
// Convergence mirrors the backend (syncBrandOrderPros 2c): only SHARED,
// non-denied casting statuses are bump targets; only casting-flow projects
// (>= Expert Proposals) are bumped; a denied project is never advanced.
const SHARED_CASTING_STATUSES = new Set([
    "Experts Proposal Review",
    "Experts Proposal Review Denied",
    "Experts Selection",
    "Scheduling",
]);
const DENIED_STATUS = "Experts Proposal Review Denied";
// Cancelled projects never decide which products a selection is for.
const CANCELLED_STATUS = "Cancelled";
const CASTING_FLOOR_RANK = PROJECT_STATUS_RANK["Expert Proposals"];
import { TimelineEstimator } from "./TimelineEstimator";

// Step components
import { OrderDetailsStep } from "./steps/OrderDetailsStep";
import { ProjectStartDateStep } from "./steps/ProjectStartDateStep";
import { ProjectFieldsStep } from "./steps/ProjectFieldsStep";
import { ContentTypeStep } from "./steps/ContentTypeStep";
import { ProjectAddedStep } from "./steps/ProjectAddedStep";
import { ReviewStep } from "./steps/ReviewStep";
import {
    IntakeMatchStep,
    IntakeBrandReview,
    IntakeNetworkReview,
    IntakeDecisions,
    IntakeProductRow,
    initialIntakeDecisions,
} from "./steps/IntakeMatchStep";
import { OrderPricingBreakdownData } from "../../components/Pricing/OrderPricingBreakdown";

const INTAKE_ERRORS: Record<string, string> = {
    IntakeDocumentMissing: "Attach a brief document.",
    IntakeDocumentTypeNotAllowed: "Only .xlsx and .xlsm briefs can be read.",
    IntakeDocumentTooLarge: "That file is over the 10 MB limit.",
    IntakeDocumentUnreadable: "That file could not be opened as a spreadsheet.",
    IntakeDocumentEmpty: "That spreadsheet has no readable content.",
    IntakeProviderUnavailable: "The extraction service is not configured.",
    IntakeExtractionFailed: "Nothing could be extracted from that document.",
    IntakeBrandMissing: "Pick a brand before submitting.",
    IntakeProductUnresolved: "A project points at a product that is no longer in the brief. Pick its product again.",
    IntakeProjectsMissing: "Add at least one project.",
};

// A product read from the brief that does not exist yet. It rides through the
// form under a negative placeholder id and is only created on submit.
interface IntakeNewProduct {
    placeholder_id: number;
    name: string;
    short_name: string;
    pdp_url: string;
}

// A new supplier brand on a retailer/agency order: stands in for the
// project's brand until the API creates it on submit.
const INTAKE_SUPPLIER_PLACEHOLDER = -1;

// "YYYY-MM-DD" as local midnight. new Date("YYYY-MM-DD") is UTC midnight,
// which shows as the previous day west of UTC.
const parseDateOnly = (value: string | null | undefined): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

// Step order: order-details → project-start-date → project-content-type → project-fields → project-added → review
type Step =
    | "order-details"
    | "project-start-date"
    | "project-content-type"
    | "project-fields"
    | "project-added"
    | "intake-match"
    | "review";

interface MultiStepOrderModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
    mode?: "create" | "edit";
    orderId?: number;
    initialOrderName?: string;
    initialBrand?: any | null;
    initialSOW?: { id: number; name: string } | null;
    initialProjects?: OrderProjectValues[];
    initialSlackChannel?: string;
    initialDownloadPage?: boolean;
    initialPricingTotal?: string | null;
    initialPricingBreakdown?: OrderPricingBreakdownData | null;
    initialPricingSnapshottedAt?: string | null;
}

interface OrderFormValues {
    order_name: string;
    brand: any | null;
    sow: any | null;
    projects: OrderProjectValues[];
    new_project_name: string;
    new_content_type: string;
    new_channel: string;
    new_rmn_brand: string | number | null;
    new_product_name: string;
    new_project_start_date: Date | null;
    // Internal-only manual price adjustment, staged as a string ("" = unset).
    new_production_adjustment: string;
    // Internal-only price lock (estimated → actual).
    new_price_locked: boolean;
    order_content_type: string;
    order_rmn_brand: string | number | null;
    slack_channel: string;
    create_slack_channel: boolean;
    new_slack_channel_name: string;
    start_date: Date | null;
    download_page: boolean;
}

const validationSchema = Yup.object().shape({
    order_name: Yup.string().required("Order name is required"),
    brand: Yup.mixed().nullable(),
    projects: Yup.array().of(
        Yup.object().shape({
            project_name: Yup.string().required("Project name is required"),
        })
    ),
});

/**
 * Bridge that runs the debounced live pricing preview from inside the Formik
 * tree (where `values.projects` lives) and lifts the result up to the modal so
 * it can be passed to the review/added steps. Renders nothing.
 */
const PricingPreviewSync: FC<{
    projects: OrderProjectValues[];
    brandId?: number;
    enabled: boolean;
    sowId?: number | null;
    onChange: (p: PricingPreviewState | null) => void;
}> = ({ projects, brandId, enabled, sowId, onChange }) => {
    const { pricingPreview } = usePricingPreview(projects, brandId, enabled, sowId);
    useEffect(() => {
        onChange(pricingPreview);
    }, [pricingPreview, onChange]);
    return null;
};

export const MultiStepOrderModal: FC<MultiStepOrderModalProps> = ({
    open,
    onClose,
    onSuccess,
    mode = "create",
    orderId,
    initialOrderName,
    initialBrand,
    initialSOW,
    initialProjects,
    initialSlackChannel,
    initialDownloadPage,
    initialPricingTotal,
    initialPricingBreakdown,
    initialPricingSnapshottedAt,
}) => {
    const { role, user, brand: userBrand } = useUser();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isBrandRole = isBrand(role) || isRMN(user);
    // Prefer the RMN brand for RMN users so the order defaults to the RMN flow;
    // the brand array has no guaranteed order, so brand[0] is unreliable.
    const userBrandData =
        userBrand?.find((b: any) => b?.rmn) || userBrand?.[0] || null;
    const effectiveBrand = isBrandRole ? initialBrand || userBrandData : initialBrand;

    // Live pricing preview for the create flow (draft projects aren't saved yet,
    // so there's no snapshot to read). Populated by <PricingPreviewSync/>.
    const [pricingPreview, setPricingPreview] =
        useState<PricingPreviewState | null>(null);

    // ── Step state ────────────────────────────────────────────────
    const [currentStep, setCurrentStep] = useState<Step>(
        mode === "edit" ? "review" : "order-details"
    );
    const [justAddedIndex, setJustAddedIndex] = useState<number>(0);

    // ── Project editing state ─────────────────────────────────────
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [deletedProjectIds, setDeletedProjectIds] = useState<number[]>([]);
    const [returnStepAfterEdit, setReturnStepAfterEdit] = useState<Step | null>(null);
    // Expert selection processes (brand_order_pros) + per-project assignment.
    const [orderPros, setOrderPros] = useState<OrderProProcess[]>([
        { key: "default", name: "" },
    ]);
    const [assignmentByProjectId, setAssignmentByProjectId] = useState<
        Record<number, number>
    >({});
    const orderProKeyCounterRef = useRef(0);
    // Edit-mode selection-process fetch state: Save is gated on "ready" so a
    // save can never run against a half-loaded process list (#12).
    const [orderProsStatus, setOrderProsStatus] = useState<
        "loading" | "ready" | "error"
    >("ready");
    // Pending "these projects will move forward" confirmation before a save that
    // merges behind projects onto a more-advanced selection.
    const [bumpConfirm, setBumpConfirm] = useState<
        { name: string; from: string; to: string }[] | null
    >(null);
    // Set true only after the user confirms the bump-forward dialog, so the
    // handleSubmit guard proceeds on the next submit (covers the button AND an
    // Enter-key implicit form submit).
    const bumpConfirmedRef = useRef(false);

    // ── Product state ─────────────────────────────────────────────
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

    // ── Channel state ─────────────────────────────────────────────
    const [selectedChannels, setSelectedChannels] = useState<ContentChannelOption[]>([]);

    // ── Brand state ───────────────────────────────────────────────
    const [selectedBrand, setSelectedBrand] = useState<any>(effectiveBrand || null);
    const [initialBrandId] = useState<number | null>(effectiveBrand?.id || null);
    const [selectedProjectBrand, setSelectedProjectBrand] = useState<any>(null);

    // ── Content type state ────────────────────────────────────────
    const [selectedContentTypeId, setSelectedContentTypeId] = useState<number | null>(null);

    // ── Slack state ───────────────────────────────────────────────
    // Fetched once here so the Review step can resolve channel IDs → names and
    // so SlackChannelPicker can share the list instead of fetching again.
    // The create-channel flag/name live as Formik fields (create_slack_channel /
    // new_slack_channel_name), managed by SlackChannelPicker.
    const [slackChannels, setSlackChannels] = useState<{ label: string; value: string }[]>([]);
    const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);

    // ── UI state ──────────────────────────────────────────────────
    const [intakeDraft, setIntakeDraft] = useState<any>(null);
    const [intakeStaged, setIntakeStaged] = useState<any>(null);
    const [intakeBusy, setIntakeBusy] = useState(false);
    const [intakeStageIndex, setIntakeStageIndex] = useState(-1);
    const [intakeSummary, setIntakeSummary] = useState("");
    const [intakeFileName, setIntakeFileName] = useState("");
    // Supplier name from the brief when no existing brand matched it. Applies
    // only while no brand is picked; created on submit.
    const [intakeBrandName, setIntakeBrandName] = useState("");
    const [intakeNewProducts, setIntakeNewProducts] = useState<IntakeNewProduct[]>([]);
    // Match step: brand suggestions from the parse, the user's last choices,
    // and whether they have been confirmed (submit is blocked until then).
    const [intakeReview, setIntakeReview] = useState<IntakeBrandReview | null>(null);
    const [intakeNetworkReview, setIntakeNetworkReview] = useState<IntakeNetworkReview | null>(null);
    const [intakeDecisions, setIntakeDecisions] = useState<IntakeDecisions | null>(null);
    const [intakeConfirmed, setIntakeConfirmed] = useState(false);
    const canImportBrief = isAdmin(role) || isAM(role);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [isProjectFieldsSubmitting, setIsProjectFieldsSubmitting] = useState(false);
    const [returnToReviewOnOrderDetailsNext, setReturnToReviewOnOrderDetailsNext] = useState(false);
    // Tracks whether the project-added screen should say "Updated" vs "Added".
    // Cannot use editingIndex because clearEditorCtx resets it before the step renders.
    const [justEditedProject, setJustEditedProject] = useState(false);

    // ── Refs ──────────────────────────────────────────────────────
    // isDirtyRef tracks whether the form has meaningful data (read in close handler without needing Formik context)
    const isDirtyRef = useRef(false);
    // formikRef enables imperative resetForm without enableReinitialize
    const formikRef = useRef<any>(null);
    // prevProjectsRef detects when useEnrichedProjects async enrichment completes
    const prevProjectsRef = useRef<OrderProjectValues[] | null>(null);

    // ── Data hooks ────────────────────────────────────────────────
    const { contentTypes, loading: contentTypesLoading } = useContentTypes(selectedBrand);
    // Channels are fetched after content type is selected (step 3 → step 4)
    const { channels, loading: channelsLoading } = useChannels(selectedBrand, selectedContentTypeId, contentTypes);
    const { rmnBrandOptions } = useRmnBrands(selectedBrand);
    const { sows: fetchedSOWs, loading: sowsLoading } = useSOWsByBrand(selectedBrand);
    const projectsInitial = useEnrichedProjects(mode, initialProjects);

    const sowOptions = useMemo(() => {
        const options = [...fetchedSOWs];
        if (initialSOW && !options.some((s) => s.id === initialSOW.id)) {
            options.unshift({ id: initialSOW.id, name: initialSOW.name, url: "" });
        }
        return options.map((s) => ({ label: s.name, value: s.id }));
    }, [fetchedSOWs, initialSOW]);

    const initialValues: OrderFormValues = useMemo(
        () => ({
            order_name: initialOrderName || "",
            brand: effectiveBrand || null,
            sow: initialSOW?.id || null,
            projects: projectsInitial,
            new_project_name: "",
            new_content_type: "",
            new_channel: "",
            new_rmn_brand: null,
            new_product_name: "",
            new_production_adjustment: "",
            new_price_locked: false,
            order_content_type: "",
            order_rmn_brand: null,
            slack_channel: initialSlackChannel || "",
            create_slack_channel: false,
            new_slack_channel_name: "",
            start_date: new Date(),
            new_project_start_date: new Date(),
            download_page: initialDownloadPage ?? false,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [initialOrderName, effectiveBrand, initialSOW, projectsInitial, initialSlackChannel, initialDownloadPage]
    );

    // ── Effects ───────────────────────────────────────────────────
    useEffect(() => {
        if (isBrandRole) {
            setSelectedBrand(initialBrand || userBrandData);
        } else {
            setSelectedBrand(initialBrand || null);
        }
    }, [initialBrand, isBrandRole, userBrandData]);

    useEffect(() => {
        setSelectedContentTypeId(null);
    }, [selectedBrand?.rmn, selectedBrand?.id]);

    useEffect(() => {
        if (isInternal(role)) {
            (async () => {
                setSlackChannelsLoading(true);
                try {
                    const res = await axiosInstant.get("/brand-projects/slack/channels");
                    setSlackChannels(res.data.map((ch: any) => ({ label: ch.name, value: ch.id })));
                } catch {
                    setSlackChannels([]);
                } finally {
                    setSlackChannelsLoading(false);
                }
            })();
        }
    }, [role]);

    // Reset all volatile state when modal opens fresh
    useEffect(() => {
        if (open) {
            setCurrentStep(mode === "edit" ? "review" : "order-details");
            setShowDiscardDialog(false);
            setIsProjectFieldsSubmitting(false);
            setReturnToReviewOnOrderDetailsNext(false);
            setJustEditedProject(false);
            setEditingIndex(null);
            setDeletedProjectIds([]);
            setJustAddedIndex(0);
            setSelectedProduct(null);
            setSelectedProducts([]);
            setSelectedChannels([]);
            setSelectedContentTypeId(null);
            setSelectedBrand(effectiveBrand || null);
            setSelectedProjectBrand(null);
            setIntakeDraft(null);
            setIntakeStaged(null);
            setIntakeSummary("");
            setIntakeFileName("");
            setIntakeBrandName("");
            setIntakeNewProducts([]);
            setIntakeReview(null);
            setIntakeNetworkReview(null);
            setIntakeDecisions(null);
            setIntakeConfirmed(false);
            prevProjectsRef.current = null;
            formikRef.current?.resetForm({ values: initialValues });
        }
    // initialValues intentionally omitted — we only want to reset on open change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode]);

    // Load / reset the order's selection processes when the modal opens. In edit
    // mode fetch the full order (getBrandOrder returns order_pros + each project's
    // brand_order_pros_id); in create mode start with one default process.
    useEffect(() => {
        if (!open) return;
        orderProKeyCounterRef.current = 0;
        if (mode === "edit" && orderId) {
            // Saving against a half-loaded (or failed) process list would send
            // a degenerate desired-state and flatten the order's selection
            // structure — track the load state and gate Save on it.
            setOrderProsStatus("loading");
            (async () => {
                try {
                    const res = await axiosInstant.get(getBrandOrder(orderId));
                    const pros: OrderProProcess[] = (res.data?.order_pros || []).map(
                        (op: any) => ({ key: String(op.id), id: op.id, name: op.name || "", locked: !!op.locked })
                    );
                    setOrderPros(pros.length ? pros : [{ key: "default", name: "" }]);
                    const map: Record<number, number> = {};
                    for (const p of res.data?.projects || []) {
                        if (p.brand_order_pros_id != null) map[p.id] = p.brand_order_pros_id;
                    }
                    setAssignmentByProjectId(map);
                    setOrderProsStatus("ready");
                } catch {
                    setOrderPros([]);
                    setAssignmentByProjectId({});
                    setOrderProsStatus("error");
                }
            })();
        } else {
            setOrderPros([{ key: "default", name: "" }]);
            setAssignmentByProjectId({});
            setOrderProsStatus("ready");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, orderId]);

    // When useEnrichedProjects async enrichment completes, merge the enriched
    // project data into the form WITHOUT resetting it — a wholesale resetForm
    // here silently discarded selection-chip assignments (and any other edits,
    // e.g. a typed order name) made while the per-project fetches ran.
    useEffect(() => {
        if (prevProjectsRef.current === null) {
            prevProjectsRef.current = projectsInitial;
            return;
        }
        if (prevProjectsRef.current === projectsInitial) return;
        prevProjectsRef.current = projectsInitial;
        if (currentStep === "review" && formikRef.current) {
            const current: OrderProjectValues[] =
                formikRef.current.values?.projects ?? [];
            const merged = (initialValues.projects || []).map((enriched) => {
                const cur = current.find(
                    (p) =>
                        p.project_id != null &&
                        p.project_id === enriched.project_id
                );
                return cur?.order_pro_key
                    ? { ...enriched, order_pro_key: cur.order_pro_key }
                    : enriched;
            });
            // DRAFT projects added while enrichment ran exist only in the
            // form (no project_id) — keep them, in order, after the
            // persisted rows.
            const drafts = current.filter((p) => p.project_id == null);
            formikRef.current.setFieldValue("projects", [
                ...merged,
                ...drafts,
            ]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectsInitial]);

    // ── Close handler ─────────────────────────────────────────────
    const handleCloseRequest = useCallback(() => {
        if (isDirtyRef.current) setShowDiscardDialog(true);
        else onClose();
    }, [onClose]);

    // ── Helpers ───────────────────────────────────────────────────
    const clearEditor = useCallback(
        (setFieldValue: (f: string, v: any) => void, startDate?: Date | null) => {
            setEditingIndex(null);
            setFieldValue("new_project_name", "");
            setFieldValue("new_channel", "");
            setSelectedChannels([]);
            setFieldValue("new_project_start_date", startDate ?? new Date());
            setFieldValue("new_content_type", "");
            setFieldValue("new_rmn_brand", null);
            setFieldValue("new_product_name", "");
            setFieldValue("new_production_adjustment", "");
            setFieldValue("new_price_locked", false);
            setSelectedProduct(null);
            setSelectedProducts([]);
            setSelectedProjectBrand(null);
            setSelectedContentTypeId(null);
        },
        []
    );

    const buildProjectPayload = useCallback(
        (
            values: OrderFormValues,
            existingProjectId: number | null = null
        ): OrderProjectValues => {
            const projectName = toStringValue(values.order_name);
            const ct = values.new_content_type;
            const rmn = values.new_rmn_brand;
            const effProducts = selectedProducts;
            const channelIds = selectedChannels.map((ch: any) => ch.id);
            return {
                project_name: projectName,
                project_id: existingProjectId,
                content_type: toStringValue(ct),
                content_type_id: ct ? Number(ct) : null,
                channel: selectedChannels.length > 0 ? selectedChannels[0].name : "",
                channel_id: channelIds.length > 0 ? channelIds[0] : null,
                channel_ids: channelIds,
                channel_objects: [...selectedChannels],
                channel_name: selectedChannels.map((ch: any) => ch.name).join(", "),
                rmn_brand_id: typeof rmn === "number" ? rmn : rmn ? Number(rmn) : null,
                product_name:
                    effProducts.length > 0
                        ? effProducts.map((p: any) => p.title).join(", ")
                        : selectedProduct?.title || "",
                product_id:
                    effProducts.length > 0
                        ? effProducts[0]?.desireid ?? effProducts[0]?.id ?? null
                        : selectedProduct?.desireid ?? selectedProduct?.id ?? null,
                product_ids:
                    effProducts.length > 0
                        ? effProducts.map((p: any) => p.desireid ?? p.id).filter(Boolean)
                        : selectedProduct?.desireid
                        ? [selectedProduct.desireid]
                        : [],
                // Kept so labels can name the first product and count the rest.
                products: (effProducts.length > 0
                    ? effProducts
                    : [selectedProduct].filter(Boolean)
                ).map((p: any) => ({
                    desireid: p.desireid ?? p.id,
                    id: p.id ?? p.desireid,
                    title: p.title || "",
                    image: p.image,
                })),
                product_image:
                    effProducts.length > 0
                        ? effProducts[0]?.image ?? null
                        : selectedProduct?.image ?? null,
                start_date: values.new_project_start_date,
                production_adjustment:
                    values.new_production_adjustment === "" ||
                    values.new_production_adjustment == null
                        ? null
                        : Number(values.new_production_adjustment),
                price_locked: values.new_price_locked,
            };
        },
        [selectedChannels, selectedProducts, selectedProduct]
    );

    // ── Selection-process helpers ─────────────────────────────────
    const removeOrderPro = (key: string) =>
        setOrderPros((prev) =>
            prev.length <= 1 ? prev : prev.filter((op) => op.key !== key)
        );
    const renameOrderPro = (key: string, name: string) =>
        setOrderPros((prev) =>
            prev.map((op) => (op.key === key ? { ...op, name } : op))
        );

    // Selection processes are scoped by product SET: every project on a process
    // must carry exactly the same products (orderProjectProductSet).
    const productSetOf = orderProjectProductSet;

    // Raw (stored) process assignment — explicit form choice, else the persisted
    // edit-mode assignment. Used to derive a process's product below.
    const rawProKey = (p: OrderProjectValues): string | null => {
        if (p.order_pro_key) return p.order_pro_key;
        if (p.project_id != null && assignmentByProjectId[p.project_id] != null) {
            return String(assignmentByProjectId[p.project_id]);
        }
        return null;
    };

    // Each persisted project's product set as LOADED (the DB state). A saved
    // selection's products come from what its projects had when the modal
    // opened, never from whichever project happens to be listed first.
    const openingProductSetById = useMemo(() => {
        const map: Record<number, string | null> = {};
        (projectsInitial || []).forEach((p: OrderProjectValues) => {
            if (p.project_id != null) map[p.project_id] = productSetOf(p);
        });
        return map;
    }, [projectsInitial, productSetOf]);

    const loadedProKey = (p: OrderProjectValues): string | null =>
        p.project_id != null && assignmentByProjectId[p.project_id] != null
            ? String(assignmentByProjectId[p.project_id])
            : null;

    // True when a persisted project's products differ from what it loaded with.
    const productsChanged = (p: OrderProjectValues): boolean =>
        p.project_id != null &&
        p.project_id in openingProductSetById &&
        openingProductSetById[p.project_id] !== productSetOf(p);

    const sameProject = (a: OrderProjectValues, b: OrderProjectValues) =>
        a === b || (a.project_id != null && a.project_id === b.project_id);

    // Can `p` sit on process `key`? Mirrors the API's product-set guard:
    //  - a project still on the selection it loaded with, products unchanged,
    //    always may (selections mixed before the product-set rule stay valid);
    //  - otherwise every OTHER live project on the selection must carry p's
    //    current set. An empty selection takes anything; cancelled projects
    //    don't count. A whole selection changing products together passes,
    //    and its saves apply the change to the selection at once.
    const canJoinProcess = (
        p: OrderProjectValues,
        key: string,
        projects: OrderProjectValues[]
    ): boolean => {
        const pid = productSetOf(p);
        if (pid == null) return true;
        if (loadedProKey(p) === key && !productsChanged(p)) return true;
        return projects.every((q) => {
            if (sameProject(q, p) || rawProKey(q) !== key) return true;
            if (q.status === CANCELLED_STATUS) return true;
            const qid = productSetOf(q);
            return qid == null || qid === pid;
        });
    };

    // Whether any live project with products is (raw-)assigned to `key`.
    const processHasProducts = (
        key: string,
        projects: OrderProjectValues[],
        except: OrderProjectValues
    ): boolean =>
        projects.some(
            (q) =>
                !sameProject(q, except) &&
                rawProKey(q) === key &&
                q.status !== CANCELLED_STATUS &&
                productSetOf(q) != null
        );

    // The process key a project resolves to. Product-aware: an explicit/persisted
    // choice wins only if that process is empty or same-product; otherwise the
    // first compatible process.
    const effectiveProKey = (p: OrderProjectValues): string => {
        const projects: OrderProjectValues[] =
            formikRef.current?.values?.projects ?? [];
        const pid = productSetOf(p);
        const compatible = (key: string): boolean =>
            canJoinProcess(p, key, projects);
        if (
            p.order_pro_key &&
            orderPros.some((op) => op.key === p.order_pro_key) &&
            compatible(p.order_pro_key)
        ) {
            return p.order_pro_key;
        }
        if (p.project_id != null && assignmentByProjectId[p.project_id] != null) {
            const k = String(assignmentByProjectId[p.project_id]);
            if (orderPros.some((op) => op.key === k) && compatible(k)) return k;
        }
        // Default: this product's own process (auto-group), else first compatible.
        if (pid != null && orderPros.some((op) => op.key === `prod-${pid}`)) {
            return `prod-${pid}`;
        }
        // Auto-consolidate only onto an EXISTING, non-empty, same-product
        // process. An empty process (no product yet) is deliberately NOT an
        // auto-target: otherwise a single freshly-added empty selection would
        // absorb every unassigned project regardless of product — hiding the
        // mismatch until the backend rejects the save. Empty selections are
        // filled by EXPLICIT assignment (the chip), which sets order_pro_key
        // above. A project with no product yet (a separate, earlier validation)
        // falls back to the first process so it isn't spuriously flagged.
        const firstCompat = orderPros.find((op) =>
            pid == null
                ? true
                : processHasProducts(op.key, projects, p) &&
                  canJoinProcess(p, op.key, projects),
        );
        // No same-product process exists — e.g. the project's only selection was
        // deleted. Leave it UNASSIGNED ("") so the Review step flags it, rather
        // than silently dropping it on a different-product process (which the
        // backend rejects: one product per selection).
        return firstCompat?.key ?? "";
    };

    // Preview which projects the backend will bump FORWARD on save: within each
    // selection (projects grouped by resolved process key), any project behind
    // the most-advanced casting stage among its siblings converges up to it.
    // Mirrors syncBrandOrderPros's convergence; never targets Pre-Production+.
    const computeBumpUps = (): { name: string; from: string; to: string }[] => {
        const projects: OrderProjectValues[] =
            formikRef.current?.values?.projects ?? [];
        const byProc = new Map<string, OrderProjectValues[]>();
        projects.forEach((p) => {
            if (!p.project_name?.trim()) return;
            const key = effectiveProKey(p);
            if (!key) return; // unassigned — flagged separately
            if (!byProc.has(key)) byProc.set(key, []);
            byProc.get(key)!.push(p);
        });
        const bumps: { name: string; from: string; to: string }[] = [];
        byProc.forEach((projs) => {
            if (projs.length < 2) return;
            // Target = most-advanced SHARED, non-denied casting status present.
            let maxRank = -1;
            let maxStatus: string | null = null;
            projs.forEach((p) => {
                const st = p.status ?? "";
                if (!SHARED_CASTING_STATUSES.has(st) || st === DENIED_STATUS) {
                    return;
                }
                const r = PROJECT_STATUS_RANK[st];
                if (r != null && r > maxRank) {
                    maxRank = r;
                    maxStatus = st;
                }
            });
            if (maxStatus == null) return; // no shared casting target here
            projs.forEach((p) => {
                const st = p.status ?? "";
                if (st === DENIED_STATUS) return; // denied projects are not advanced
                const r = PROJECT_STATUS_RANK[st];
                if (r != null && r >= CASTING_FLOOR_RANK && r < maxRank) {
                    bumps.push({
                        name: p.project_name || "Project",
                        from: p.status || "—",
                        to: maxStatus as string,
                    });
                }
            });
        });
        return bumps;
    };

    // Create mode: seed one default selection process per distinct product set
    // when the user reaches Review (auto-group). Internal users can then add
    // extra same-products processes to split a set across shoots.
    useEffect(() => {
        if (mode !== "create" || currentStep !== "review") return;
        const projects: OrderProjectValues[] =
            formikRef.current?.values?.projects ?? [];
        const products = Array.from(
            new Set(
                projects
                    .filter((p) => p.project_name?.trim())
                    .map((p) => productSetOf(p))
                    .filter((x): x is string => x != null)
            )
        );
        if (products.length === 0) return;
        // A product-set edit after an earlier Review visit leaves that set's
        // auto selection empty — drop auto selections no project resolves to
        // or explicitly picked, so they don't eat the one-per-project cap.
        const liveAutoKeys = new Set(products.map((pid) => `prod-${pid}`));
        const pickedKeys = new Set(
            projects.map((p) => p.order_pro_key).filter((k): k is string => !!k)
        );
        setOrderPros((prev) => {
            prev = prev.filter(
                (op) =>
                    !op.key.startsWith("prod-") ||
                    liveAutoKeys.has(op.key) ||
                    pickedKeys.has(op.key)
            );
            const have = new Set(prev.map((op) => op.key));
            const additions = products
                .filter((pid) => !have.has(`prod-${pid}`))
                .map((pid) => ({ key: `prod-${pid}`, name: "" }));
            const hasProd =
                additions.length > 0 ||
                prev.some((op) => op.key.startsWith("prod-"));
            if (additions.length === 0 && !hasProd) return prev;
            // Drop the initial placeholder "default" once real product processes
            // exist; keep any user-added split processes.
            return [...prev, ...additions].filter((op) => op.key !== "default");
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, currentStep]);

    // ── Submit ────────────────────────────────────────────────────
    const handleIntakeFile = async (
        file: File,
        setFieldValue: (field: string, value: any) => void
    ) => {
        if (intakeBusy) return;
        setIntakeBusy(true);
        setIntakeFileName(file.name);
        setIntakeStageIndex(0);
        const stageTimers = [
            setTimeout(() => setIntakeStageIndex(1), 1200),
            setTimeout(() => setIntakeStageIndex(2), 3000),
            setTimeout(() => setIntakeStageIndex(3), 14000),
        ];
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await axiosInstant.post(parseIntakeDocument(), form);
            const { draft, staged, brand_review: review, network_review: networkReview } = data;
            const rows: IntakeProductRow[] = (draft.products || []).map((p: any) => ({
                name: p.name,
                short_name: p.short_name || "",
                pdp_url: p.pdp_url || "",
                desireid: p.desireid || null,
                candidates: p.candidates || [],
            }));
            const rowIndexByName = new Map<string, number>(rows.map((r, i) => [r.name, i]));
            const orderStart = parseDateOnly(draft.order.start_date);
            // Projects start without a product: the match step decides each
            // one, and applyIntakeDecisions fills them in.
            const projects = (draft.projects || []).map((p: any, index: number) => {
                const productIndex = rowIndexByName.get(p.product_name);
                // One project per product + content type; each cut is a
                // channel instance (repeats allowed: In-Store 1, In-Store 2).
                const channelIds: number[] = p.content_channel_ids || [];
                const channelNames: string[] = p.content_channel_names || [];
                const channelObjects = channelIds.map((id, i) => ({ id, name: channelNames[i] || "" }));
                return {
                    id: `intake-${index}`,
                    project_name: p.title,
                    project_id: null,
                    content_type: p.content_type_id ? String(p.content_type_id) : "",
                    content_type_id: p.content_type_id || null,
                    channel: channelObjects.length > 0 ? channelObjects[0].name : "",
                    channel_id: channelIds.length > 0 ? channelIds[0] : null,
                    channel_ids: channelIds,
                    channel_objects: channelObjects,
                    channel_name: channelObjects.map((ch) => ch.name).join(", "),
                    rmn_brand_id: null,
                    product_name: p.product_name || "",
                    product_id: null,
                    product_ids: [],
                    products: [],
                    product_image: null,
                    start_date: parseDateOnly(p.start_date) || orderStart || new Date(),
                    production_adjustment: null,
                    price_locked: false,
                    intake_product: productIndex == null ? null : productIndex,
                };
            });

            setFieldValue("order_name", draft.order.name || "");
            setFieldValue("projects", projects);
            if (orderStart) setFieldValue("start_date", orderStart);
            setIntakeDraft(draft);
            setIntakeStaged(staged || null);
            setIntakeReview(review);
            setIntakeNetworkReview(networkReview);
            setIntakeDecisions(initialIntakeDecisions(networkReview, review, rows));
            setIntakeConfirmed(false);
            setIntakeNewProducts([]);
            setIntakeBrandName("");
            setCurrentStep("intake-match");
            setIntakeSummary(
                `${file.name} read${networkReview?.name ? ` (${networkReview.name})` : ""}: ${projects.length} project${
                    projects.length === 1 ? "" : "s"
                }, ${rows.length} product${rows.length === 1 ? "" : "s"}. Nothing is created until you submit.`
            );
        } catch (e: any) {
            const code = e?.response?.data?.error;
            toast.error(INTAKE_ERRORS[code] || "That document could not be read.");
        } finally {
            stageTimers.forEach(clearTimeout);
            setIntakeStageIndex(-1);
            setIntakeBusy(false);
        }
    };

    // Match step "Confirm": set the brand (or the pending new one) and point
    // every brief-drafted project at its chosen product. New products ride as
    // negative placeholder ids until submit.
    const applyIntakeDecisions = (
        decisions: IntakeDecisions,
        values: OrderFormValues,
        setFieldValue: (field: string, value: any) => void
    ) => {
        const brandDecision = decisions.brand;
        if (!brandDecision) return;
        // A retailer/agency owns the order and the supplier rides on each
        // project (rmn_brand_id here; the payload builder turns it into the
        // project's brand). Otherwise the supplier owns the order.
        const network = decisions.network?.kind === "existing" ? decisions.network.brand : null;
        const supplier = brandDecision.kind === "existing" ? brandDecision.brand : null;
        const nextBrand = network || supplier;
        const projectSupplierId = network ? (supplier ? supplier.id : INTAKE_SUPPLIER_PLACEHOLDER) : null;
        if ((values.brand?.id ?? null) !== (nextBrand?.id ?? null)) {
            setFieldValue("brand", nextBrand);
            setFieldValue("sow", null);
            setFieldValue("slack_channel", "");
            setFieldValue("create_slack_channel", false);
            setFieldValue("new_slack_channel_name", "");
            setSelectedBrand(nextBrand);
        }
        setIntakeBrandName(brandDecision.kind === "new" ? brandDecision.name.trim() : "");

        const newProducts: IntakeNewProduct[] = [];
        const resolved = decisions.rows.map((row, i) => {
            const decision = decisions.products[i];
            if (decision?.kind === "existing") {
                return { desireid: decision.product.desireid, title: decision.product.title, image: decision.product.image || null };
            }
            const placeholder = -(i + 1);
            const name = decision?.kind === "new" ? decision.name.trim() : row.name;
            newProducts.push({ placeholder_id: placeholder, name, short_name: row.short_name, pdp_url: row.pdp_url });
            return { desireid: placeholder, title: name, image: null };
        });
        setIntakeNewProducts(newProducts);

        setFieldValue(
            "projects",
            values.projects.map((p) => {
                if (p.intake_product == null || !resolved[p.intake_product]) return p;
                const product = resolved[p.intake_product];
                return {
                    ...p,
                    rmn_brand_id: projectSupplierId,
                    product_name: product.title,
                    product_id: product.desireid,
                    product_ids: [product.desireid],
                    products: [{ desireid: product.desireid, id: product.desireid, title: product.title, image: product.image || undefined }],
                    product_image: product.image,
                };
            })
        );
        setIntakeDecisions(decisions);
        setIntakeConfirmed(true);
        setCurrentStep("review");
    };

    // "3M → Sam's Club" when submitting will add the supplier to the
    // retailer/agency's brands (not already linked, or a new supplier).
    const intakeNetworkLink = (): string | null => {
        const network = intakeDecisions?.network?.kind === "existing" ? intakeDecisions.network.brand : null;
        const supplier = intakeDecisions?.brand;
        if (!network || !supplier) return null;
        if (supplier.kind === "existing" && (supplier.brand.network_ids || []).includes(network.id)) return null;
        const name = supplier.kind === "existing" ? supplier.brand.name : supplier.name.trim();
        return `${name} → ${network.name}`;
    };

    const handleSubmit = async (values: OrderFormValues, actions: FormikHelpers<OrderFormValues>) => {
        const brandId = values.brand?.id ?? selectedBrand?.id;
        // Brief intake: the brand and products must be confirmed on the match
        // step, and still agree with the brand on the form.
        if (intakeDraft && intakeDecisions) {
            const confirmedNetwork = intakeDecisions.network?.kind === "existing" ? intakeDecisions.network.brand : null;
            const confirmedSupplier = intakeDecisions.brand?.kind === "existing" ? intakeDecisions.brand.brand : null;
            const expectedOrderBrandId = (confirmedNetwork || confirmedSupplier)?.id ?? null;
            const brandMoved = (brandId ?? null) !== expectedOrderBrandId;
            if (!intakeConfirmed || brandMoved) {
                if (brandMoved && values.brand) {
                    // Carry the form's brand into the match step: a network
                    // becomes the order brand, anything else the supplier.
                    const picked = { id: values.brand.id, name: values.brand.name, logo: values.brand.logo, rmn: values.brand.rmn };
                    setIntakeDecisions(
                        picked.rmn
                            ? { ...intakeDecisions, network: { kind: "existing", brand: picked } }
                            : { ...intakeDecisions, network: { kind: "none" }, brand: { kind: "existing", brand: picked } }
                    );
                }
                setIntakeConfirmed(false);
                toast.error(
                    brandMoved
                        ? "The brand changed since you matched products. Check the matches again."
                        : "Confirm the brand and products first."
                );
                setCurrentStep("intake-match");
                return;
            }
        }
        const pendingBrandName = !brandId && intakeDraft ? intakeBrandName : "";
        if (!brandId && !pendingBrandName) {
            toast.error("Brand is required");
            return;
        }

        // Edit mode must never save against a half-loaded (or failed) process
        // list — the sync payload would flatten the order's selections (#12).
        if (mode === "edit" && orderProsStatus !== "ready") {
            toast.error(
                orderProsStatus === "loading"
                    ? "Still loading the order's selections — try again in a moment."
                    : "Couldn't load the order's selections. Close and reopen the order to save."
            );
            return;
        }

        const projectsWithName = values.projects.filter((p) => p.project_name?.trim());
        // Only NEW projects must carry a product here. An EXISTING project can
        // show an empty product when its enrichment fetch failed — the server
        // already has its real product, and updateExistingProject omits the
        // product fields rather than sending null (#27).
        const projectsMissingProduct = projectsWithName.filter(
            (p) =>
                p.project_id == null &&
                (p.product_ids?.length ?? 0) === 0 &&
                p.product_id == null
        );
        if (projectsMissingProduct.length > 0) {
            toast.error("Each project must have a product selected.");
            return;
        }

        // Product-scoped selections: an internal user's multi-project order can
        // leave a project with no compatible selection (effectiveProKey -> ""),
        // e.g. after deleting its only same-product selection. The Review Submit
        // button disables for this, but an implicit form submit (Enter in the
        // process-name field) would bypass the button — so guard here too, the
        // same way projectsMissingProduct does, before anything is sent.
        const projectsMissingSelection =
            isInternal(role) && projectsWithName.length > 1
                ? projectsWithName.filter((p) => effectiveProKey(p) === "")
                : [];
        if (projectsMissingSelection.length > 0) {
            toast.error(
                `${projectsMissingSelection.length} project${
                    projectsMissingSelection.length !== 1 ? "s" : ""
                } need a selection before saving.`
            );
            return;
        }

        // Merging behind projects onto a more-advanced selection bumps them
        // forward (backend convergence). Confirm first — unless the user already
        // did via the dialog. Guarded here (not just the Submit button) so an
        // Enter-key implicit form submit can't skip the warning.
        if (isInternal(role) && !bumpConfirmedRef.current) {
            const bumps = computeBumpUps();
            if (bumps.length > 0) {
                setBumpConfirm(bumps);
                actions.setSubmitting(false);
                return;
            }
        }
        bumpConfirmedRef.current = false;

        const isRmnOrder = !!selectedBrand?.rmn;
        const sowId = typeof values.sow === "number" ? values.sow : null;

        try {
            if (mode === "create") {
                // Honor the internal user's process organization: each project
                // goes to its resolved process (the product's default, or an
                // explicit same-product split), and we send the processes they
                // actually used with their names. Same-product is enforced by the
                // selector and validated server-side; brand users have no process
                // UI, so the backend auto-groups their order by product instead.
                const projectsPayload = projectsWithName.map((p) => ({
                    ...p,
                    order_pro_key: effectiveProKey(p),
                }));
                const usedKeys = Array.from(
                    new Set(projectsPayload.map((p) => p.order_pro_key))
                );
                const orderProsPayload = usedKeys.map((key) => ({
                    key,
                    name: orderPros.find((op) => op.key === key)?.name || null,
                }));
                if (intakeDraft) {
                    // Same order body as a manual create, plus what the brief
                    // adds. The brand and products only exist after this call.
                    const order = buildOrderWithProjectsPayload({
                        brandId: brandId ?? null,
                        orderName: values.order_name,
                        sowId,
                        slackChannel: values.slack_channel || null,
                        createSlackChannel: values.create_slack_channel,
                        newSlackChannelName: values.new_slack_channel_name,
                        downloadPage: values.download_page,
                        isRmnOrder,
                        orderPros: orderProsPayload,
                        projects: projectsPayload,
                    });
                    const usedIds = new Set(
                        projectsPayload.flatMap((p) =>
                            p.product_ids?.length ? p.product_ids : p.product_id != null ? [p.product_id] : []
                        )
                    );
                    try {
                        await axiosInstant.post(createIntakeOrder(), {
                            order: { ...order, description: intakeDraft.order?.description || "" },
                            supplier_brand_id:
                                intakeDecisions?.brand?.kind === "existing" ? intakeDecisions.brand.brand.id : null,
                            new_brand_name:
                                intakeDecisions?.brand?.kind === "new" ? intakeDecisions.brand.name.trim() : null,
                            new_products: intakeNewProducts.filter((p) => usedIds.has(p.placeholder_id)),
                            brief: intakeDraft.brief,
                            staged: intakeStaged,
                        });
                    } catch (e: any) {
                        const code = e?.response?.data?.error;
                        toast.error(INTAKE_ERRORS[code] || code || "The order could not be created.");
                        return;
                    }
                    toast.success("Order created from the brief.");
                    if (onSuccess) await onSuccess();
                    onClose();
                    return;
                }
                // One atomic call: order + selection processes + projects.
                await createOrderWithProjects({
                    brandId,
                    orderName: values.order_name,
                    sowId,
                    slackChannel: values.slack_channel || null,
                    createSlackChannel: values.create_slack_channel,
                    newSlackChannelName: values.new_slack_channel_name,
                    downloadPage: values.download_page,
                    isRmnOrder,
                    orderPros: orderProsPayload,
                    projects: projectsPayload,
                });
                toast.success("Order created successfully.");
                if (onSuccess) await onSuccess();
                onClose();
                return;
            }

            // ── edit mode ──
            if (!orderId) return;
            const resolvedSlackChannel = values.slack_channel || null;
            const existingProjects = projectsWithName.filter((p) => p.project_id != null);
            const newProjects = projectsWithName.filter((p) => !p.project_id);

            // A project on a LOCKED selection gets a reduced payload (SOW-frozen
            // fields omitted — see updateExistingProject). Resolved from the
            // derived `locked` flag on the order's processes.
            const isProjectLocked = (p: OrderProjectValues) =>
                !!orderPros.find((op) => op.key === effectiveProKey(p))?.locked;

            // Projects share a selection only while their products match, and
            // the API never moves a project to fix a mismatch — it rejects the
            // save. So a project whose products change AND that changes
            // selection is saved WITHOUT its products first, the sync moves
            // it, and only then are its products written (on the new
            // selection they match). A project whose whole selection changes
            // products together sends apply_products_to_selection.
            const movesWithNewProducts = (p: OrderProjectValues) =>
                isInternal(role) &&
                productsChanged(p) &&
                loadedProKey(p) !== effectiveProKey(p);
            const appliesToSelection = (p: OrderProjectValues) =>
                isInternal(role) &&
                productsChanged(p) &&
                loadedProKey(p) === effectiveProKey(p);

            // Project-level updates run FIRST so a rejection can't leave a
            // half-saved order — the order-level update commits LAST.
            if (existingProjects.length && initialBrandId) {
                await Promise.all(
                    existingProjects.map((p) =>
                        updateExistingProject(initialBrandId, p, brandId, isRmnOrder, isProjectLocked(p), {
                            skipProducts: movesWithNewProducts(p),
                            applyProductsToSelection: appliesToSelection(p),
                        })
                    )
                );
            }
            if (deletedProjectIds.length && initialBrandId) {
                await unlinkDeletedProjects(initialBrandId, deletedProjectIds);
            }

            // Create brand-new projects, capturing their ids so the sync below
            // can assign them to a selection process.
            const createdNew: { p: OrderProjectValues; id: number }[] = [];
            if (newProjects.length) {
                const results = await Promise.all(
                    newProjects.map((p) =>
                        createNewProject(brandId, p, orderId, isRmnOrder, resolvedSlackChannel)
                    )
                );
                results.forEach((res: any, i: number) => {
                    if (res?.id != null) createdNew.push({ p: newProjects[i], id: res.id });
                });
            }

            // Sync selection processes + project assignments (internal only —
            // the endpoint is internal-gated).
            if (isInternal(role)) {
                const orderProsPayload = orderPros.map((op) =>
                    op.id != null
                        ? { id: op.id, name: op.name || null }
                        : { key: op.key, name: op.name || null }
                );
                const buildAssignment = (projectId: number, key: string) => {
                    const proc = orderPros.find((op) => op.key === key);
                    if (!proc) return null;
                    return proc.id != null
                        ? { project_id: projectId, order_pro_id: proc.id }
                        : { project_id: projectId, order_pro_key: proc.key };
                };
                const assignments: {
                    project_id: number;
                    order_pro_id?: number;
                    order_pro_key?: string;
                    desireids?: number[];
                }[] = [];
                for (const p of existingProjects) {
                    if (p.project_id == null) continue;
                    const a = buildAssignment(p.project_id, effectiveProKey(p));
                    // Products this project saves after the move — the sync's
                    // same-products guard checks them, not the stored ones.
                    if (a && movesWithNewProducts(p) && p.product_ids?.length) {
                        assignments.push({ ...a, desireids: p.product_ids });
                    } else if (a) assignments.push(a);
                }
                for (const { p, id } of createdNew) {
                    const a = buildAssignment(id, effectiveProKey(p));
                    if (a) assignments.push(a);
                }
                // Persisted processes always ship (omitting one would delete
                // it); NEW keys ship only when a project actually uses them, so
                // stray empty selections are never persisted.
                const usedKeys = new Set(
                    assignments
                        .map((a) => a.order_pro_key)
                        .filter((k): k is string => k != null)
                );
                const prosPayload = orderProsPayload.filter(
                    (op: any) => op.id != null || usedKeys.has(op.key)
                );
                await syncOrderPros(orderId, prosPayload, assignments);

                // Products deferred above, now that each project sits on the
                // selection its new products belong to.
                const deferred = existingProjects.filter(movesWithNewProducts);
                if (deferred.length && initialBrandId) {
                    await Promise.all(
                        deferred.map((p) =>
                            updateExistingProject(initialBrandId, p, brandId, isRmnOrder, isProjectLocked(p))
                        )
                    );
                }
            }

            // Order-level fields last — see ordering note above.
            await updateOrder(
                orderId,
                brandId,
                values.order_name,
                sowId,
                values.slack_channel || null,
                values.create_slack_channel,
                values.new_slack_channel_name,
                values.download_page
            );

            toast.success("Order updated successfully.");
            if (onSuccess) await onSuccess();
            onClose();
        } catch (err: any) {
            if (err?.response?.status === 409) {
                toast.error("Project name already exists!");
            } else {
                toast.error(err?.response?.data?.error ?? err?.message ?? "Failed to save order");
            }
        } finally {
            actions.setSubmitting(false);
        }
    };

    const modalTitle =
        mode === "edit"
            ? isBrandRole
                ? "Edit Request"
                : "Edit Order"
            : isBrandRole
            ? "Request A Quote"
            : "New Order";

    return (
        <>
            <Dialog open={open} onClose={handleCloseRequest} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}>
                <DialogTitle sx={{ p: 0 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        px={{ xs: 2, md: 3 }}
                        py={2}
                        borderBottom="1px solid var(--border)"
                    >
                        <Typography
                            sx={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3, fontFamily: "CircularStd" }}
                        >
                            {modalTitle}
                        </Typography>
                        <IconButton onClick={handleCloseRequest} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, setFieldValue, isSubmitting, submitForm }) => {
                        // Track dirty state so the close handler (outside Formik context) can read it
                        isDirtyRef.current = !!values.order_name?.trim() || values.projects.length > 0;

                        // ── Shared helpers that need Formik context ────────────
                        const clearEditorCtx = () => clearEditor(setFieldValue, values.start_date);

                        // Load edit project into form fields
                        const loadProjectForEdit = (index: number) => {
                            const project = values.projects[index];
                            setEditingIndex(index);
                            setFieldValue("new_project_name", project.project_name || "");
                            setFieldValue("new_channel", project.channel_id ?? project.channel ?? "");
                            setSelectedChannels(project.channel_objects || []);
                            setFieldValue("new_project_start_date", project.start_date ?? values.start_date);
                            setFieldValue("new_content_type", project.content_type_id ?? project.content_type ?? "");
                            setFieldValue("new_rmn_brand", project.rmn_brand_id ?? null);
                            setFieldValue(
                                "new_production_adjustment",
                                project.production_adjustment != null
                                    ? String(project.production_adjustment)
                                    : ""
                            );
                            setFieldValue("new_price_locked", project.price_locked ?? false);
                            const ctId = project.content_type_id ?? (project.content_type ? Number(project.content_type) : null);
                            setSelectedContentTypeId(ctId);

                            const matchedBrand = project.rmn_brand_id
                                ? rmnBrandOptions.find(
                                      (b) => b.id === project.rmn_brand_id || b.id?.toString() === project.rmn_brand_id?.toString()
                                  )
                                : null;
                            setSelectedProjectBrand(matchedBrand || null);

                            // Set both selectedProduct and selectedProducts (matches old form handleEdit).
                            // Prefer the enriched products (real titles/images); fall back to
                            // product_ids only when enrichment hasn't populated them.
                            if (project.products?.length) {
                                const products = project.products.map((p) => ({
                                    desireid: p.desireid,
                                    id: p.id,
                                    title: p.title || "",
                                    image: p.image,
                                }));
                                setSelectedProducts(products);
                                setSelectedProduct(products[0] || null);
                            } else if (project.product_ids?.length) {
                                const products = project.product_ids.map((id: number, i: number) => ({
                                    desireid: id,
                                    id,
                                    title: i === 0 ? project.product_name || "" : `Product ${id}`,
                                }));
                                setSelectedProducts(products);
                                setSelectedProduct(products[0] || null);
                            } else if (project.product_id) {
                                const singleProduct = {
                                    desireid: project.product_id,
                                    id: project.product_id,
                                    title: project.product_name || "",
                                };
                                setSelectedProducts([singleProduct]);
                                setSelectedProduct(singleProduct);
                            } else {
                                setSelectedProducts([]);
                                setSelectedProduct(null);
                            }
                        };

                        return (
                            <Form>
                                {/* Debounced live pricing preview for the create
                                    flow — kept mounted across all steps so the
                                    estimate stays warm. */}
                                <PricingPreviewSync
                                    projects={values.projects}
                                    brandId={effectiveBrand?.id}
                                    enabled={!isBrandRole && mode === "create"}
                                    sowId={typeof values.sow === "number" ? values.sow : null}
                                    onChange={setPricingPreview}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        height: { xs: "100%", md: "65vh" },
                                        overflow: "hidden",
                                        width: { xs: "100%", md: "900px" },
                                        maxWidth: "100%",
                                    }}
                                >
                                    {/* Left panel (top on mobile): Timeline Estimator */}
                                    <Box
                                        sx={{
                                            width: { xs: "100%", md: 280 },
                                            flexShrink: 0,
                                            borderRight: { xs: "none", md: "1px solid var(--border)" },
                                            borderBottom: { xs: "1px solid var(--border)", md: "none" },
                                            maxHeight: { xs: "38vh", md: "none" },
                                            py: { xs: 1.5, md: 0 },
                                            overflow: "hidden",
                                        }}
                                    >
                                        <TimelineEstimator numProjects={values.projects.length} />
                                    </Box>

                                    {/* Right panel: active step */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            minWidth: 0,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <FieldArray name="projects">
                                            {({ push, remove, replace }) => {
                                                // Content type selection: set type only — Next button advances
                                                const handleContentTypeSelect = (ct: { id: number; name: string; [key: string]: any }) => {
                                                    setFieldValue("new_content_type", ct.id);
                                                    setSelectedContentTypeId(ct.id);
                                                };

                                                // Project fields "Next": resolve async brand/product, build payload, commit project
                                                const handleProjectFieldsNext = () => {
                                                    // Capture before clearEditorCtx resets it — React 18 batches all updates
                                                    const wasEditing = editingIndex !== null;
                                                    setIsProjectFieldsSubmitting(true);
                                                    try {
                                                        const existingProjectId =
                                                            editingIndex !== null
                                                                ? values.projects[editingIndex]?.project_id ?? null
                                                                : null;
                                                        const payload = buildProjectPayload(
                                                            values,
                                                            existingProjectId
                                                        );
                                                        const newIndex = editingIndex !== null ? editingIndex : values.projects.length;
                                                        if (editingIndex !== null) {
                                                            // Keep the loaded status: selection previews
                                                            // (bumps, cancelled siblings) read it.
                                                            replace(editingIndex, {
                                                                ...payload,
                                                                status: values.projects[editingIndex]?.status ?? null,
                                                            });
                                                        } else {
                                                            push(payload);
                                                        }
                                                        const returnTo = returnStepAfterEdit;
                                                        setReturnStepAfterEdit(null);
                                                        if (!returnTo) setJustAddedIndex(newIndex);
                                                        setJustEditedProject(wasEditing);
                                                        clearEditorCtx();
                                                        setCurrentStep(returnTo ?? "project-added");
                                                    } catch {
                                                        toast.error("Something went wrong. Please try again.");
                                                    } finally {
                                                        setIsProjectFieldsSubmitting(false);
                                                    }
                                                };

                                                const handleEditProject = (index: number) => {
                                                    loadProjectForEdit(index);
                                                    setReturnStepAfterEdit(currentStep === "review" ? "review" : null);
                                                    setCurrentStep("project-start-date");
                                                };

                                                const handleRemoveProject = (index: number) => {
                                                    const project = values.projects[index];
                                                    if (mode === "edit" && project?.project_id) {
                                                        setDeletedProjectIds((prev) =>
                                                            prev.includes(project.project_id!)
                                                                ? prev
                                                                : [...prev, project.project_id!]
                                                        );
                                                    }
                                                    remove(index);
                                                    if (editingIndex !== null) {
                                                        if (editingIndex === index) {
                                                            clearEditorCtx();
                                                        } else if (index < editingIndex) {
                                                            setEditingIndex(editingIndex - 1);
                                                        }
                                                    }
                                                    if (index < justAddedIndex) {
                                                        setJustAddedIndex(justAddedIndex - 1);
                                                    }
                                                };

                                                switch (currentStep) {
                                                    case "order-details":
                                                        return (
                                                            <OrderDetailsStep
                                                                values={values}
                                                                errors={errors}
                                                                touched={touched}
                                                                setFieldValue={setFieldValue}
                                                                isBrandRole={isBrandRole}
                                                                isInternalRole={isInternal(role)}
                                                                selectedBrand={selectedBrand}
                                                                setSelectedBrand={setSelectedBrand}
                                                                setSelectedChannels={setSelectedChannels}
                                                                setSelectedContentTypeId={setSelectedContentTypeId}
                                                                sowOptions={sowOptions}
                                                                sowsLoading={sowsLoading}
                                                                slackChannels={slackChannels}
                                                                slackChannelsLoading={slackChannelsLoading}
                                                                selectedSow={fetchedSOWs.find((s) => s.id === values.sow) || null}
                                                                mode={mode}
                                                                onIntakeFile={
                                                                    mode === "create" && canImportBrief
                                                                        ? (file: File) =>
                                                                              handleIntakeFile(file, setFieldValue)
                                                                        : undefined
                                                                }
                                                                intakeBusy={intakeBusy}
                                                                intakeStageIndex={intakeStageIndex}
                                                                intakeFileName={intakeFileName}
                                                                intakeSummary={intakeSummary}
                                                                pendingBrandName={
                                                                    intakeDraft && !values.brand ? intakeBrandName : ""
                                                                }
                                                                onNext={() => {
                                                                    if (returnToReviewOnOrderDetailsNext) {
                                                                        setReturnToReviewOnOrderDetailsNext(false);
                                                                        setCurrentStep("review");
                                                                    } else {
                                                                        setCurrentStep("project-start-date");
                                                                    }
                                                                }}
                                                                onCancel={handleCloseRequest}
                                                            />
                                                        );

                                                    case "project-start-date":
                                                        return (
                                                            <ProjectStartDateStep
                                                                orderName={values.order_name}
                                                                projectNumber={
                                                                    editingIndex !== null
                                                                        ? editingIndex + 1
                                                                        : values.projects.length + 1
                                                                }
                                                                startDate={values.new_project_start_date}
                                                                onDateChange={(date) =>
                                                                    setFieldValue("new_project_start_date", date)
                                                                }
                                                                onBack={() => {
                                                                    if (editingIndex !== null) {
                                                                        const backStep = returnStepAfterEdit ?? (values.projects.length > 0 ? "project-added" : "order-details");
                                                                        setReturnStepAfterEdit(null);
                                                                        clearEditorCtx();
                                                                        setCurrentStep(backStep);
                                                                    } else if (returnStepAfterEdit) {
                                                                        const backStep = returnStepAfterEdit;
                                                                        setReturnStepAfterEdit(null);
                                                                        setCurrentStep(backStep);
                                                                    } else {
                                                                        setCurrentStep(values.projects.length > 0 ? "project-added" : "order-details");
                                                                    }
                                                                }}
                                                                onNext={() => setCurrentStep("project-content-type")}
                                                            />
                                                        );

                                                    case "project-content-type":
                                                        return (
                                                            <ContentTypeStep
                                                                contentTypes={contentTypes}
                                                                contentTypesLoading={contentTypesLoading}
                                                                selectedContentTypeId={values.new_content_type}
                                                                onSelect={handleContentTypeSelect}
                                                                onBack={() => setCurrentStep("project-start-date")}
                                                                onNext={() => setCurrentStep("project-fields")}
                                                            />
                                                        );

                                                    case "project-fields":
                                                        return (
                                                            <ProjectFieldsStep
                                                                orderBrand={selectedBrand}
                                                                selectedBrandName={
                                                                    !!selectedBrand?.rmn && selectedProjectBrand?.name
                                                                        ? selectedProjectBrand.name
                                                                        : selectedBrand?.name
                                                                }
                                                                isRMNOrder={!!selectedBrand?.rmn}
                                                                rmnBrandOptions={rmnBrandOptions}
                                                                channels={channels}
                                                                selectedChannels={selectedChannels}
                                                                onChannelsChange={setSelectedChannels}
                                                                channelsLoading={channelsLoading}
                                                                selectedProduct={selectedProduct}
                                                                setSelectedProduct={setSelectedProduct}
                                                                selectedProducts={selectedProducts}
                                                                onProductsChange={setSelectedProducts}
                                                                selectedRmnBrandId={values.new_rmn_brand}
                                                                selectedProjectBrand={selectedProjectBrand}
                                                                setSelectedProjectBrand={setSelectedProjectBrand}
                                                                allowAddBrand={isInternal(role) || isRMN(user)}
                                                                setFieldValue={setFieldValue}
                                                                showAdjustment={
                                                                    !isBrandRole &&
                                                                    editingIndex !== null &&
                                                                    Boolean(values.projects[editingIndex]?.project_id)
                                                                }
                                                                productionAdjustment={values.new_production_adjustment}
                                                                priceLocked={values.new_price_locked}
                                                                isLoading={isProjectFieldsSubmitting}
                                                                orderName={values.order_name}
                                                                projectNumber={editingIndex !== null ? editingIndex + 1 : values.projects.length + 1}
                                                                onBack={() => setCurrentStep("project-content-type")}
                                                                onNext={handleProjectFieldsNext}
                                                            />
                                                        );

                                                    case "project-added":
                                                        return (
                                                            <ProjectAddedStep
                                                                orderName={values.order_name}
                                                                projects={values.projects}
                                                                brandId={values.brand?.id}
                                                                brand={values.brand}
                                                                justAddedIndex={justAddedIndex}
                                                                contentTypes={contentTypes}
                                                                isEditing={justEditedProject}
                                                                onEdit={handleEditProject}
                                                                onRemove={handleRemoveProject}
                                                                onAddAnother={() => {
                                                                    clearEditorCtx();
                                                                    setCurrentStep("project-start-date");
                                                                }}
                                                                onReview={() => setCurrentStep("review")}
                                                                pricingByKey={pricingPreview?.byKey}
                                                            />
                                                        );

                                                    case "intake-match":
                                                        return intakeReview && intakeDecisions ? (
                                                            <IntakeMatchStep
                                                                key={JSON.stringify(intakeDecisions.brand)}
                                                                networkReview={
                                                                    intakeNetworkReview || { name: "", match_id: null, candidates: [] }
                                                                }
                                                                review={intakeReview}
                                                                decisions={intakeDecisions}
                                                                onBack={() => setCurrentStep("order-details")}
                                                                onConfirm={(decisions) =>
                                                                    applyIntakeDecisions(decisions, values, setFieldValue)
                                                                }
                                                            />
                                                        ) : null;

                                                    case "review":
                                                        return (
                                                            <ReviewStep
                                                                intakeNotes={intakeDraft?.notes || []}
                                                                onEditIntakeMatches={
                                                                    intakeReview ? () => setCurrentStep("intake-match") : undefined
                                                                }
                                                                intakeCreates={
                                                                    intakeDraft
                                                                        ? {
                                                                              brandName:
                                                                                  intakeDecisions?.brand?.kind === "new"
                                                                                      ? intakeDecisions.brand.name.trim()
                                                                                      : "",
                                                                              link: intakeNetworkLink(),
                                                                              products: intakeNewProducts.filter((np) =>
                                                                                  values.projects.some(
                                                                                      (p) =>
                                                                                          p.project_name?.trim() &&
                                                                                          (p.product_ids?.length
                                                                                              ? p.product_ids
                                                                                              : [p.product_id]
                                                                                          ).includes(np.placeholder_id)
                                                                                  )
                                                                              ),
                                                                          }
                                                                        : null
                                                                }
                                                                values={values}
                                                                projects={values.projects}
                                                                contentTypes={contentTypes}
                                                                sowOptions={sowOptions}
                                                                slackChannels={slackChannels}
                                                                slackChannelsLoading={slackChannelsLoading}
                                                                isBrandRole={isBrandRole}
                                                                isInternalRole={isInternal(role)}
                                                                orderPros={orderPros}
                                                                onAddOrderPro={() => {
                                                                    // Add a selection. If any projects are still
                                                                    // unassigned, house the FIRST unassigned project's
                                                                    // product group into the new selection. Otherwise
                                                                    // create an EMPTY selection the user can split a
                                                                    // project into via its chip — a product may span up
                                                                    // to one selection per project (different shoot
                                                                    // dates, requirements, etc.). The button is capped
                                                                    // at one selection per project in ReviewStep.
                                                                    const firstUnassigned = values.projects.find(
                                                                        (p) => p.project_name?.trim() && effectiveProKey(p) === ""
                                                                    );
                                                                    const key = `new-${orderProKeyCounterRef.current++}`;
                                                                    setOrderPros((prev) => [...prev, { key, name: "" }]);
                                                                    if (!firstUnassigned) return;
                                                                    const targetProduct = productSetOf(firstUnassigned);
                                                                    values.projects.forEach((p, idx) => {
                                                                        if (
                                                                            p.project_name?.trim() &&
                                                                            effectiveProKey(p) === "" &&
                                                                            productSetOf(p) === targetProduct
                                                                        ) {
                                                                            setFieldValue(`projects.${idx}.order_pro_key`, key);
                                                                        }
                                                                    });
                                                                }}
                                                                onRemoveOrderPro={removeOrderPro}
                                                                onRenameOrderPro={renameOrderPro}
                                                                getProjectProKey={effectiveProKey}
                                                                canAssignProject={(p, key) =>
                                                                    canJoinProcess(p, key, values.projects)
                                                                }
                                                                onAssignProject={(index, key) => {
                                                                    // Per-project (same-product enforced by the selector) so
                                                                    // an internal user can split a product's projects across
                                                                    // processes; unmoved projects stay on the product default.
                                                                    setFieldValue(`projects.${index}.order_pro_key`, key);
                                                                }}
                                                                onAddOrderProForProject={(index) => {
                                                                    // New same-product process for THIS project only (a split),
                                                                    // e.g. when one project films on a later date. Capped at
                                                                    // one selection per project, same as Add Process.
                                                                    if (orderPros.length >= values.projects.length) return;
                                                                    const key = `new-${orderProKeyCounterRef.current++}`;
                                                                    setOrderPros((prev) => [...prev, { key, name: "" }]);
                                                                    setFieldValue(`projects.${index}.order_pro_key`, key);
                                                                }}
                                                                isSubmitting={isSubmitting}
                                                                processesReady={orderProsStatus === "ready"}
                                                                mode={mode}
                                                                pricingTotal={
                                                                    mode === "create"
                                                                        ? pricingPreview?.order?.total != null
                                                                            ? String(pricingPreview.order.total)
                                                                            : null
                                                                        : initialPricingTotal
                                                                }
                                                                pricingBreakdown={
                                                                    mode === "create"
                                                                        ? pricingPreview?.order?.breakdown ?? null
                                                                        : initialPricingBreakdown
                                                                }
                                                                pricingSnapshottedAt={
                                                                    mode === "create" ? undefined : initialPricingSnapshottedAt
                                                                }
                                                                pricingByKey={pricingPreview?.byKey}
                                                                onBack={
                                                                    mode === "edit"
                                                                        ? undefined
                                                                        : () => setCurrentStep(values.projects.length > 0 ? "project-added" : "order-details")
                                                                }
                                                                onEditOrderDetails={() => {
                                                                    setReturnToReviewOnOrderDetailsNext(true);
                                                                    setCurrentStep("order-details");
                                                                }}
                                                                onEditProject={handleEditProject}
                                                                onRemoveProject={handleRemoveProject}
                                                                onAddProject={() => {
                                                                    clearEditorCtx();
                                                                    setReturnStepAfterEdit("review");
                                                                    setCurrentStep("project-start-date");
                                                                }}
                                                                onSubmit={submitForm}
                                                            />
                                                        );

                                                    default:
                                                        return null;
                                                }
                                            }}
                                        </FieldArray>
                                    </Box>
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </Dialog>

            {/* Discard confirmation dialog */}
            <Dialog open={showDiscardDialog} onClose={() => setShowDiscardDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
                <DialogTitle sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: 17, pb: 1 }}>
                    Discard this order?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                        You'll lose any details you've entered.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setShowDiscardDialog(false)}
                        sx={{ textTransform: "none", fontFamily: "CircularStd", color: "var(--text)" }}
                    >
                        Keep editing
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => { setShowDiscardDialog(false); onClose(); }}
                        sx={{ textTransform: "none", fontFamily: "CircularStd", borderRadius: "8px" }}
                    >
                        Discard
                    </Button>
                </DialogActions>
            </Dialog>

            {/* "These projects will move forward" confirmation for a merge that
                bumps behind projects up to the selection's furthest-along stage. */}
            {bumpConfirm && (
                <ConfirmationDialog
                    open
                    title="Move projects forward?"
                    description={[
                        "Some projects are earlier in casting than others on the same selection. Saving will move them forward to match the selection's furthest-along project:",
                        ...bumpConfirm.map((b) => `${b.name}:  ${b.from}  →  ${b.to}`),
                    ]}
                    confirmLabel="Move & save"
                    cancelLabel="Cancel"
                    onClose={() => setBumpConfirm(null)}
                    onConfirm={() => {
                        bumpConfirmedRef.current = true;
                        setBumpConfirm(null);
                        formikRef.current?.submitForm();
                    }}
                />
            )}
        </>
    );
};
